import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  UserPlus, Building2, FileText, Shield, DollarSign, Search,
  CheckCircle, XCircle, Upload, Loader2, ChevronLeft, ChevronRight, Eye
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CustomerCreation() {
  const { user } = useAuth();
  const token = localStorage.getItem("sendtoo_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const [tab, setTab] = useState("list");

  return (
    <DashboardLayout title="Admin Panel" user={user} portalColor="red">
      <div className="p-4 space-y-4" data-testid="customer-creation-page">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Customer Management</h1>
          <Button onClick={() => setTab(tab === "create" ? "list" : "create")} data-testid="toggle-create-btn">
            {tab === "create" ? "Back to List" : <><UserPlus className="w-4 h-4 mr-1" /> Create Customer</>}
          </Button>
        </div>
        {tab === "list" && <CustomerList headers={headers} token={token} onViewProfile={(id) => setTab(`profile-${id}`)} />}
        {tab === "create" && <CreateCustomerForm headers={headers} token={token} onSuccess={() => setTab("list")} />}
        {tab.startsWith("profile-") && <CustomerProfile customerId={tab.replace("profile-", "")} headers={headers} token={token} onBack={() => setTab("list")} />}
      </div>
    </DashboardLayout>
  );
}

function CustomerList({ headers, token, onViewProfile }) {
  const [profiles, setProfiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const fetch_ = useCallback(async () => {
    const params = new URLSearchParams({ page: page.toString(), page_size: "15" });
    if (search) params.set("search", search);
    if (typeFilter) params.set("customer_type", typeFilter);
    const r = await fetch(`${API}/api/v2/customers/profiles?${params}`, { headers });
    if (r.ok) { const d = await r.json(); setProfiles(d.items); setTotal(d.total); }
  }, [page, search, typeFilter]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const [counts, setCounts] = useState({ retail: null, enterprise: null });
  useEffect(() => {
    (async () => {
      const out = {};
      for (const t of ["retail", "enterprise"]) {
        try {
          const r = await fetch(`${API}/api/v2/customers/profiles?page=1&page_size=1&customer_type=${t}`, { headers });
          out[t] = r.ok ? (await r.json()).total : null;
        } catch { out[t] = null; }
      }
      setCounts(out);
    })();
  }, []);

  const groupCards = [
    { key: "retail", title: "B2C \u2014 Retail", sub: "Individuals. Invoices kept per shipment; GST filed as one lumpsum entry per month.", tone: "border-gray-300 bg-gray-50" },
    { key: "enterprise", title: "B2B \u2014 Enterprise, Business, Company", sub: "GST-registered customers. Invoice-level GST (CGST+SGST or IGST) per customer.", tone: "border-blue-300 bg-blue-50" },
  ];

  return (
    <div data-testid="customer-list">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4" data-testid="customer-group-cards">
        {groupCards.map(g => (
          <button key={g.key} type="button" data-testid={`group-card-${g.key}`}
            onClick={() => { setTypeFilter(typeFilter === g.key ? "" : g.key); setPage(1); }}
            className={`text-left rounded-lg border p-4 transition-all hover:shadow-sm ${g.tone} ${typeFilter === g.key ? "ring-2 ring-brand-500" : ""}`}>
            <div className="flex items-center justify-between">
              <span className="font-semibold text-slate-900">{g.title}</span>
              <span className="text-2xl font-bold text-slate-900">{counts[g.key] ?? "\u2014"}</span>
            </div>
            <p className="text-xs text-slate-600 mt-1">{g.sub}</p>
            <p className="text-[11px] text-slate-500 mt-1">{typeFilter === g.key ? "Showing this group \u2014 click again to clear" : "Click to filter the list"}</p>
          </button>
        ))}
      </div>
      <div className="flex gap-3 mb-4">
        <select className="border rounded px-3 py-2 text-sm" value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
          <option value="">All Types</option>
          <option value="retail">Retail</option>
          <option value="enterprise">Enterprise</option>
          <option value="partner">Partner</option>
        </select>
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, GST..." className="max-w-xs" />
        <Button size="sm" variant="outline" onClick={() => { setPage(1); fetch_(); }}><Search className="w-4 h-4" /></Button>
        <span className="text-sm text-gray-500 self-center">{total} customers</span>
      </div>
      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
            <tr>
              <th className="px-3 py-2 text-left">Contact</th>
              <th className="px-3 py-2 text-left">Company</th>
              <th className="px-3 py-2 text-center">Type</th>
              <th className="px-3 py-2 text-left">GST</th>
              <th className="px-3 py-2 text-left">PAN</th>
              <th className="px-3 py-2 text-center">KYC</th>
              <th className="px-3 py-2 text-center">Margin</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {profiles.map(p => (
              <tr key={p.customer_id} className="hover:bg-gray-50">
                <td className="px-3 py-2">
                  <div className="font-medium">{p.contact_person}</div>
                  <div className="text-xs text-gray-500">{p.email}</div>
                </td>
                <td className="px-3 py-2">{p.company_name || "—"}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${p.customer_type === "enterprise" ? "bg-blue-100 text-blue-700" : p.customer_type === "partner" ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-700"}`}>
                    {p.customer_type}{p.partner_type ? ` (${p.partner_type})` : ""}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{p.gst_number || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{p.pan_number || "—"}</td>
                <td className="px-3 py-2 text-center">
                  {p.pan_verified ? <CheckCircle className="w-4 h-4 text-green-600 inline" /> : <XCircle className="w-4 h-4 text-gray-300 inline" />}
                </td>
                <td className="px-3 py-2 text-center">{p.margin_percent != null ? `${p.margin_percent}%` : "Global"}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="outline" onClick={() => onViewProfile(p.customer_id)} data-testid={`view-profile-${p.customer_id}`}>
                    <Eye className="w-3 h-3 mr-1" /> Profile
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > 15 && (
        <div className="flex justify-between items-center mt-3">
          <span className="text-sm text-gray-500">Page {page}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <Button size="sm" variant="outline" onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateCustomerForm({ headers, token, onSuccess }) {
  const [form, setForm] = useState({
    customer_type: "retail", partner_type: "", company_name: "", contact_person: "",
    mobile: "", email: "", password: "",
    business_type: "", gst_number: "", pan_number: "", cin_number: "", iec_number: "",
    rate_type: "GLOBAL", margin_percent: "", volumetric_divisor_b2c: "5000", volumetric_divisor_b2b: "5000",
    vendor_access: [], credit_limit: "0"
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_person || !form.email || !form.password || !form.mobile) {
      toast.error("Contact person, email, mobile, and password are required");
      return;
    }
    setSubmitting(true);
    const payload = {
      ...form,
      margin_percent: form.margin_percent ? parseFloat(form.margin_percent) : null,
      volumetric_divisor: parseInt(form.volumetric_divisor_b2c) || 5000,
      volumetric_divisor_b2b: parseInt(form.volumetric_divisor_b2b) || 5000,
      credit_limit: parseFloat(form.credit_limit) || 0,
      partner_type: form.customer_type === "partner" ? form.partner_type : null,
    };
    const r = await fetch(`${API}/api/v2/customers/create`, {
      method: "POST", headers, body: JSON.stringify(payload)
    });
    if (r.ok) {
      toast.success("Customer created successfully");
      onSuccess();
    } else {
      const err = await r.json();
      toast.error(err.detail || "Creation failed");
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={submit} className="space-y-4 max-w-4xl" data-testid="create-customer-form">
      <div className="border rounded-lg p-4 space-y-3">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2"><UserPlus className="w-4 h-4 text-blue-600" />Basic Details</h3>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Customer Type *</label>
            <select name="customer_type" className="w-full border rounded px-3 py-2 text-sm" value={form.customer_type} onChange={handleChange} data-testid="cust-type">
              <option value="retail">Retail</option>
              <option value="enterprise">Enterprise</option>
              <option value="partner">Partner</option>
            </select>
          </div>
          {form.customer_type === "partner" && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Partner Type *</label>
              <select name="partner_type" className="w-full border rounded px-3 py-2 text-sm" value={form.partner_type} onChange={handleChange} data-testid="partner-type">
                <option value="">Select</option>
                <option value="NETWORK">Network Partner</option>
                <option value="EXECUTION">Execution Partner</option>
                <option value="REVENUE">Revenue Partner</option>
              </select>
            </div>
          )}
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Company Name</label><input name="company_name" className="w-full border rounded px-3 py-2 text-sm" value={form.company_name} onChange={handleChange} /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Contact Person *</label><input name="contact_person" className="w-full border rounded px-3 py-2 text-sm" value={form.contact_person} onChange={handleChange} data-testid="cust-name" /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Mobile *</label><input name="mobile" className="w-full border rounded px-3 py-2 text-sm" value={form.mobile} onChange={handleChange} data-testid="cust-mobile" /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Email *</label><input name="email" type="email" className="w-full border rounded px-3 py-2 text-sm" value={form.email} onChange={handleChange} data-testid="cust-email" /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Password *</label><input name="password" type="password" className="w-full border rounded px-3 py-2 text-sm" value={form.password} onChange={handleChange} data-testid="cust-password" /></div>
        </div>
      </div>

      <div className="border rounded-lg p-4 space-y-3">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2"><Building2 className="w-4 h-4 text-blue-600" />Business Details</h3>
        <div className="grid grid-cols-3 gap-3">
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Business Type</label>
            <select name="business_type" className="w-full border rounded px-3 py-2 text-sm" value={form.business_type} onChange={handleChange}>
              <option value="">Select</option>
              <option value="Proprietorship">Proprietorship</option>
              <option value="Partnership">Partnership</option>
              <option value="Private Limited">Private Limited</option>
              <option value="LLP">LLP</option>
              <option value="Public Limited">Public Limited</option>
            </select>
          </div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">GST Number</label><input name="gst_number" className="w-full border rounded px-3 py-2 text-sm" value={form.gst_number} onChange={handleChange} placeholder="27AADCB2230M1ZT" data-testid="cust-gst" /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">PAN Number</label><input name="pan_number" className="w-full border rounded px-3 py-2 text-sm" value={form.pan_number} onChange={handleChange} placeholder="AADCB2230M" data-testid="cust-pan" /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">CIN (optional)</label><input name="cin_number" className="w-full border rounded px-3 py-2 text-sm" value={form.cin_number} onChange={handleChange} /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">IEC (optional)</label><input name="iec_number" className="w-full border rounded px-3 py-2 text-sm" value={form.iec_number} onChange={handleChange} /></div>
        </div>
      </div>

      <div className="border rounded-lg p-4 space-y-3">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2"><Shield className="w-4 h-4 text-blue-600" />KYC Documents</h3>
        <p className="text-xs text-gray-500">Upload after creating the customer — go to Customer Profile → KYC tab for OCR-verified document upload.</p>
      </div>

      <div className="border rounded-lg p-4 space-y-3">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2"><DollarSign className="w-4 h-4 text-blue-600" />Rate Settings</h3>
        <div className="grid grid-cols-3 gap-3">
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Rate Type</label>
            <select name="rate_type" className="w-full border rounded px-3 py-2 text-sm" value={form.rate_type} onChange={handleChange}>
              <option value="GLOBAL">Global Rate</option>
              <option value="CUSTOM">Custom Rate</option>
              <option value="PARTNER">Partner Rate</option>
            </select>
          </div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Custom Margin %</label><input name="margin_percent" type="number" step="0.5" className="w-full border rounded px-3 py-2 text-sm" value={form.margin_percent} onChange={handleChange} placeholder="Leave blank for global" /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">B2C / C2C Volumetric Divisor</label>
            <select name="volumetric_divisor_b2c" className="w-full border rounded px-3 py-2 text-sm" value={form.volumetric_divisor_b2c} onChange={handleChange}>
              <option value="5000">5000 (Standard)</option>
              <option value="4500">4500</option>
              <option value="4000">4000</option>
              <option value="3800">3800</option>
              <option value="2500">2500</option>
            </select>
          </div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">B2B Volumetric Divisor</label>
            <select name="volumetric_divisor_b2b" className="w-full border rounded px-3 py-2 text-sm" value={form.volumetric_divisor_b2b} onChange={handleChange}>
              <option value="5000">5000 (Standard)</option>
              <option value="4500">4500</option>
              <option value="4000">4000</option>
              <option value="3800">3800</option>
              <option value="2500">2500</option>
            </select>
          </div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Credit Limit (Rs.)</label><input name="credit_limit" type="number" className="w-full border rounded px-3 py-2 text-sm" value={form.credit_limit} onChange={handleChange} /></div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700">GST @ 18% is mandatory and will be applied on all invoices automatically.</div>
      </div>

      <Button type="submit" disabled={submitting} data-testid="create-customer-submit">
        {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />}
        Create Customer
      </Button>
    </form>
  );
}

function CustomerProfile({ customerId, headers, token, onBack }) {
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState("basic");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/v2/customers/profile/${customerId}`, { headers }).then(r => r.json()).then(setProfile);
  }, [customerId]);

  const uploadKyc = async (docType, enteredNumber, file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("doc_type", docType);
    formData.append("entered_number", enteredNumber);
    const r = await fetch(`${API}/api/v2/customers/kyc-upload/${customerId}`, {
      method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData
    });
    if (r.ok) {
      const d = await r.json();
      if (d.validation_match) toast.success(`${docType} verified! Extracted: ${d.extracted_number}`);
      else if (d.ocr_status === "MISMATCH") toast.error(`${docType} mismatch! Extracted: ${d.extracted_number} vs Entered: ${d.entered_number}`);
      else toast.info(`${docType} uploaded. OCR: ${d.ocr_status}`);
      // Refresh profile
      const r2 = await fetch(`${API}/api/v2/customers/profile/${customerId}`, { headers });
      if (r2.ok) setProfile(await r2.json());
    } else {
      const err = await r.json();
      toast.error(err.detail || "Upload failed");
    }
    setUploading(false);
  };

  if (!profile) return <p className="text-gray-400">Loading...</p>;

  const tabs = [
    { key: "basic", label: "Basic Info" },
    { key: "business", label: "Business" },
    { key: "kyc", label: "KYC" },
    { key: "pricing", label: "Pricing" },
    { key: "gst", label: "GST" },
  ];

  return (
    <div data-testid="customer-profile">
      <Button variant="outline" size="sm" onClick={onBack} className="mb-4"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
      <div className="flex items-center gap-4 mb-4">
        <h2 className="text-xl font-bold">{profile.contact_person}</h2>
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${profile.customer_type === "enterprise" ? "bg-blue-100 text-blue-700" : profile.customer_type === "partner" ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-700"}`}>
          {profile.customer_type}{profile.partner_type ? ` / ${profile.partner_type}` : ""}
        </span>
        <span className="text-sm text-gray-500">{profile.email}</span>
      </div>

      <div className="flex gap-1 border-b mb-4">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`px-3 py-2 text-sm font-medium border-b-2 ${activeTab === t.key ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border rounded-lg p-4">
        {activeTab === "basic" && (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-gray-500">Company:</span> {profile.company_name || "—"}</div>
            <div><span className="text-gray-500">Contact:</span> {profile.contact_person}</div>
            <div><span className="text-gray-500">Mobile:</span> {profile.mobile}</div>
            <div><span className="text-gray-500">Email:</span> {profile.email}</div>
            <div><span className="text-gray-500">Wallet:</span> Rs.{profile.wallet_balance?.toLocaleString()}</div>
            <div><span className="text-gray-500">Credit Limit:</span> Rs.{profile.credit_limit?.toLocaleString()}</div>
          </div>
        )}
        {activeTab === "business" && (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-gray-500">Business Type:</span> {profile.business_type || "—"}</div>
            <div><span className="text-gray-500">GST:</span> <span className="font-mono">{profile.gst_number || "—"}</span> {profile.gst_verified && <CheckCircle className="w-4 h-4 text-green-600 inline ml-1" />}</div>
            <div><span className="text-gray-500">PAN:</span> <span className="font-mono">{profile.pan_number || "—"}</span> {profile.pan_verified && <CheckCircle className="w-4 h-4 text-green-600 inline ml-1" />}</div>
            <div><span className="text-gray-500">CIN:</span> {profile.cin_number || "—"}</div>
            <div><span className="text-gray-500">IEC:</span> {profile.iec_number || "—"}</div>
          </div>
        )}
        {activeTab === "kyc" && (
          <div className="space-y-4">
            {["PAN", "AADHAR", "GST_CERT"].map(docType => {
              const kyc = profile.kyc_documents?.[docType];
              return (
                <div key={docType} className="border rounded p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">{docType.replace("_", " ")} Document</h4>
                    {kyc?.validation_match ? <span className="flex items-center gap-1 text-green-600 text-xs font-medium"><CheckCircle className="w-4 h-4" /> Verified</span>
                      : kyc?.ocr_status === "MISMATCH" ? <span className="flex items-center gap-1 text-red-600 text-xs font-medium"><XCircle className="w-4 h-4" /> Mismatch</span>
                      : kyc ? <span className="text-amber-600 text-xs">{kyc.ocr_status}</span>
                      : <span className="text-gray-400 text-xs">Not uploaded</span>}
                  </div>
                  {kyc?.extracted_number && <div className="text-xs text-gray-500 mb-2">OCR extracted: <span className="font-mono font-bold">{kyc.extracted_number}</span></div>}
                  <KycUploader docType={docType} enteredValue={docType === "PAN" ? profile.pan_number : docType === "GST_CERT" ? profile.gst_number : ""} onUpload={uploadKyc} uploading={uploading} />
                </div>
              );
            })}
          </div>
        )}
        {activeTab === "pricing" && (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-gray-500">Rate Type:</span> <span className="font-medium">{profile.rate_type}</span></div>
            <div><span className="text-gray-500">Margin:</span> <span className="font-bold text-blue-600">{profile.effective_margin != null ? `${profile.effective_margin}%` : "Global Default"}</span></div>
            <div><span className="text-gray-500">Vol. Divisor:</span> <span className="font-mono">{profile.volumetric_divisor}</span></div>
            <div><span className="text-gray-500">Vendor Access:</span> {profile.vendor_access?.join(", ") || "All"}</div>
          </div>
        )}
        {activeTab === "gst" && (
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded p-3">
              <div className="text-sm font-medium text-amber-800">GST @ 18% — Mandatory</div>
              <div className="text-xs text-amber-600 mt-1">GST is applied at: Customer level, Shipment level, Margin level, Surcharges, First Mile, Last Mile, Mid Mile</div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-gray-500">GST Enabled:</span> <span className="text-green-600 font-medium">Always ON</span></div>
              <div><span className="text-gray-500">GST Rate:</span> <span className="font-bold">18%</span></div>
              <div><span className="text-gray-500">GSTIN:</span> <span className="font-mono">{profile.gst_number || "Not provided"}</span></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KycUploader({ docType, enteredValue, onUpload, uploading }) {
  const [number, setNumber] = useState(enteredValue || "");
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) onUpload(docType, number, file);
  };

  return (
    <div className="flex items-center gap-2">
      <Input value={number} onChange={e => setNumber(e.target.value)} placeholder={`Enter ${docType} number`} className="w-48 text-xs" data-testid={`kyc-number-${docType}`} />
      <label className={`inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium cursor-pointer ${uploading ? "bg-gray-200 text-gray-500" : "bg-blue-600 text-white hover:bg-blue-700"}`}>
        {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
        Upload & Verify
        <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} data-testid={`kyc-upload-${docType}`} />
      </label>
    </div>
  );
}
